/* eslint-disable no-console */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Room, RoomEvent, Track, LocalAudioTrack, LocalVideoTrack, DisconnectReason } from 'livekit-client';
import {
  Loader2, AlertTriangle, Mic, Mic2, MicOff, Video, VideoOff, LogOut, Hand, CheckCircle2, Volume2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LiveChatOverlay from './LiveChatOverlay';
import useLiveKitChat from '../hooks/useLiveKitChat';
import useRemoteGuestTracks from '../hooks/useRemoteGuestTracks';
import GuestVideoTile from './GuestVideoTile';
import { joinStage, removeGuest } from '../api/lives';
import liveDiag from '../lib/liveDiag'; // [LIVE-DIAG] temporary

/**
 * LiveKitViewer — subscribes to the host's published tracks and renders
 * the realtime feed.
 *
 * Phase 76 — explicit stage-join flow (uniform on iOS/Android/desktop):
 *  - `cohostStatus` prop: null | requested | accepted | on_stage | …
 *  - When the host ACCEPTS a request the viewer connects with a publish
 *    token but does NOT stream anything yet: a big "Sali sul palco"
 *    button appears. Only the user tap triggers getUserMedia + publish
 *    (user gesture → reliable camera/mic permission on iOS Safari).
 *  - "Lascia" / the sidebar "Lascia la partecipazione" button (via the
 *    `nymia:leave-stage` window event) unpublishes + stops the local
 *    tracks and removes the guest entry → back to plain viewer. Going on
 *    stage again requires a NEW request approved by the host.
 *  - When the creator ends the live (room deleted server-side) viewers
 *    see a clean "La live è terminata" overlay instead of an error.
 */

const viewerCache = new Map(); // cacheKey -> { room, disconnectTimer, localTracks }

const cacheKeyFor = (liveId, role) => `${liveId}::${role}`;

const LiveKitViewer = ({
  live,
  onParticipantCount,
  cohostStatus = null,
  onChat = null,
  onStageChanged = null,
  onLiveEnded = null,
}) => {
  const { t } = useTranslation('lives');
  const videoRef = useRef(null);          // remote video (host)
  const audioRef = useRef(null);          // remote audio (host)
  const selfPreviewRef = useRef(null);    // local cam preview (on stage only)
  const [status, setStatus] = useState('connecting'); // connecting | live | ended | error
  const [errorMsg, setErrorMsg] = useState('');
  const [activeRoom, setActiveRoom] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  // Stage lifecycle — 'idle' (accepted, waiting for the tap) → 'publishing'
  // → 'live' (streams active) | 'error' (retry available).
  const [stage, setStage] = useState('idle');
  const [stageError, setStageError] = useState('');
  // Browser autoplay policy: remote audio may be blocked until a user gesture.
  const [audioBlocked, setAudioBlocked] = useState(false);
  const stageBusyRef = useRef(false);
  const localTracksRef = useRef({ audio: null, video: null });
  const audioOnly = live?.kind === 'audio';
  const canPublish = cohostStatus === 'accepted' || cohostStatus === 'on_stage';
  const role = canPublish ? 'cohost' : 'viewer';
  const { user: currentUser } = useAuth();

  const chat = useLiveKitChat({
    room: activeRoom,
    currentUser,
    isCreator: false,
    hostUserId: live?.creator_id,
  });
  const remoteGuests = useRemoteGuestTracks(activeRoom, live?.creator_id);
  useEffect(() => {
    if (typeof onChat === 'function') onChat(chat);
  }, [chat, onChat]);

  useEffect(() => {
    if (!live?.livekit_url || !live?.livekit_token) {
      setStatus('error');
      setErrorMsg(t('viewer.token_unavailable'));
      return undefined;
    }

    let cancelled = false;
    const cacheKey = cacheKeyFor(live.id, role);

    // ---- StrictMode/remount reuse: cancel any pending disconnect, reuse room
    const existing = viewerCache.get(cacheKey);
    if (existing?.room?.state === 'connected') {
      console.info('[LKV] reusing connected', role, 'for', live.id);
      if (existing.disconnectTimer) {
        clearTimeout(existing.disconnectTimer);
        existing.disconnectTimer = null;
      }
      const room = existing.room;
      // Only the HOST's tracks go to the main video/audio elements.
      room.remoteParticipants.forEach((p) => {
        if (p.identity !== live.creator_id) return;
        p.trackPublications.forEach((pub) => {
          if (pub.track) {
            try {
              if (pub.track.kind === Track.Kind.Video && videoRef.current) pub.track.attach(videoRef.current);
              else if (pub.track.kind === Track.Kind.Audio && audioRef.current) pub.track.attach(audioRef.current);
            } catch (e) { console.warn('[LKV] re-attach', e); }
          }
        });
      });
      localTracksRef.current = existing.localTracks || { audio: null, video: null };
      if (localTracksRef.current.audio || localTracksRef.current.video) {
        // Already streaming (remount) — restore the on-stage UI state.
        setStage('live');
        setMicOn(!!localTracksRef.current.audio && !localTracksRef.current.audio.isMuted);
        setCamOn(!!localTracksRef.current.video && !localTracksRef.current.video.isMuted);
      }
      setActiveRoom(room);
      setStatus('live');
      setAudioBlocked(!room.canPlaybackAudio);
      if (onParticipantCount) onParticipantCount(room.numParticipants);
    } else {
      // ---- Fresh connect
      const room = new Room({ adaptiveStream: true, dynacast: true });
      viewerCache.set(cacheKey, { room, disconnectTimer: null, localTracks: { audio: null, video: null } });

      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (cancelled) return;
        console.info('[LKV] audio playback allowed:', room.canPlaybackAudio);
        setAudioBlocked(!room.canPlaybackAudio);
      });

      const attach = (track, _pub, participant) => {
        if (participant && participant.identity !== live.creator_id) {
          console.info('[GUEST] non-host track — handled by guest tile', {
            identity: participant.identity, kind: track?.kind,
          });
          return;
        }
        try {
          if (track.kind === Track.Kind.Video && videoRef.current) track.attach(videoRef.current);
          else if (track.kind === Track.Kind.Audio && audioRef.current) track.attach(audioRef.current);
        } catch (e) { console.warn('[LKV] attach', e); }
      };

      room.on(RoomEvent.TrackSubscribed, attach);
      room.on(RoomEvent.ParticipantConnected, () => {
        if (onParticipantCount) onParticipantCount(room.numParticipants);
      });
      room.on(RoomEvent.ParticipantDisconnected, () => {
        if (onParticipantCount) onParticipantCount(room.numParticipants);
      });
      // [LIVE-DIAG] temporary — reconnection visibility
      room.on(RoomEvent.Reconnecting, () => liveDiag('lk_reconnecting', { live_id: live.id, role }));
      room.on(RoomEvent.Reconnected, () => liveDiag('lk_reconnected', { live_id: live.id, role }));
      room.on(RoomEvent.Disconnected, (reason) => {
        if (cancelled) return;
        if (reason === DisconnectReason.CLIENT_INITIATED) return; // expected teardown
        if (reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.SERVER_SHUTDOWN) {
          // Phase 76 — the creator ended the live: clean "ended" overlay
          // (not an error) + immediate page refresh via onLiveEnded.
          console.info('[LKV] room closed by host', reason);
          liveDiag('viewer_live_ended_overlay', { live_id: live.id, reason: String(reason) }); // [LIVE-DIAG]
          setStatus('ended');
          try { onLiveEnded?.(); } catch (_e) { /* noop */ }
          return;
        }
        setStatus('error');
        setErrorMsg(t('viewer.disconnected'));
      });

      (async () => {
        try {
          await room.connect(live.livekit_url, live.livekit_token);
          if (cancelled) return;
          setActiveRoom(room);
          setStatus('live');
          setAudioBlocked(!room.canPlaybackAudio);
          if (onParticipantCount) onParticipantCount(room.numParticipants);
          room.remoteParticipants.forEach((p) => {
            p.trackPublications.forEach((pub) => {
              if (pub.track) attach(pub.track, pub, p);
            });
          });
          // Phase 76 — NO auto-publish here. Publishing happens only in
          // goOnStage(), triggered by the explicit "Sali sul palco" tap.
        } catch (err) {
          console.error('[LKV] connect', err);
          if (cancelled) return;
          setStatus('error');
          setErrorMsg(err?.message || t('viewer.connect_error'));
        }
      })();
    }

    // ---- Cleanup: defer disconnect by a microtask. If the same live re-mounts
    // (StrictMode), the next effect cancels the timer and re-attaches.
    return () => {
      cancelled = true;
      const entry = viewerCache.get(cacheKey);
      if (!entry) return;
      if (entry.disconnectTimer) clearTimeout(entry.disconnectTimer);
      entry.disconnectTimer = setTimeout(() => {
        try {
          if (entry.localTracks?.audio) {
            try { entry.localTracks.audio.stop(); } catch (_e) { /* noop */ }
          }
          if (entry.localTracks?.video) {
            try { entry.localTracks.video.stop(); } catch (_e) { /* noop */ }
          }
          entry.room?.disconnect();
        } catch (_e) { /* noop */ }
        viewerCache.delete(cacheKey);
      }, 250);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.id, live?.livekit_token, role]);

  // [LIVE-DIAG] temporary — the viewer SEES the approval (accepted state).
  useEffect(() => {
    if (cohostStatus === 'accepted') liveDiag('participation_approved_seen', { live_id: live?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohostStatus]);

  /** Resume blocked remote audio (must run inside a user gesture). */
  const enableAudio = useCallback(async () => {
    const room = viewerCache.get(cacheKeyFor(live?.id, role))?.room;
    if (!room) return;
    try {
      await room.startAudio();
      setAudioBlocked(!room.canPlaybackAudio);
      liveDiag('viewer_audio_unlocked', { live_id: live?.id }); // [LIVE-DIAG]
    } catch (e) {
      console.warn('[LKV] startAudio failed', e);
    }
  }, [live?.id, role]);

  // Any tap/click on the page while blocked → try to unlock audio silently.
  useEffect(() => {
    if (!audioBlocked) return undefined;
    const handler = () => { enableAudio(); };
    document.addEventListener('pointerdown', handler, { capture: true });
    document.addEventListener('keydown', handler, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', handler, { capture: true });
      document.removeEventListener('keydown', handler, { capture: true });
    };
  }, [audioBlocked, enableAudio]);

  // Back to plain viewer → reset the stage UI state.
  useEffect(() => {
    if (role !== 'cohost') {
      setStage('idle');
      setStageError('');
      localTracksRef.current = { audio: null, video: null };
    }
  }, [role]);

  // Attach the local self-preview once the stage UI is rendered.
  useEffect(() => {
    const v = localTracksRef.current.video;
    if (stage === 'live' && v && selfPreviewRef.current) {
      try { v.attach(selfPreviewRef.current); } catch (_e) { /* noop */ }
    }
  }, [stage]);

  /** Explicit user-gesture stage join (uniform on iOS/Android/desktop). */
  const goOnStage = async () => {
    if (stageBusyRef.current || role !== 'cohost') return;
    stageBusyRef.current = true;
    setStage('publishing');
    setStageError('');
    liveDiag('stage_join_tap', { live_id: live?.id }); // [LIVE-DIAG]
    let stream = null;
    try {
      console.info('[GUEST] goOnStage — requesting camera/mic (user gesture)');
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: audioOnly ? false : {
          facingMode: { ideal: 'user' },
          width: { ideal: 1280 }, height: { ideal: 720 },
        },
      });
      liveDiag('media_permission_granted', { live_id: live?.id, audio: stream.getAudioTracks().length > 0, video: stream.getVideoTracks().length > 0 }); // [LIVE-DIAG]
      const entry = viewerCache.get(cacheKeyFor(live.id, 'cohost'));
      const room = entry?.room;
      // Wait (max 8s) for the publish-capable room connection.
      let waited = 0;
      while (room && room.state !== 'connected' && waited < 8000) {
        await new Promise((r) => setTimeout(r, 200));
        waited += 200;
      }
      if (!room || room.state !== 'connected') throw new Error('room_not_connected');
      const audioRaw = stream.getAudioTracks()[0];
      const videoRaw = audioOnly ? null : stream.getVideoTracks()[0];
      const audioTrack = audioRaw ? new LocalAudioTrack(audioRaw) : null;
      const videoTrack = videoRaw ? new LocalVideoTrack(videoRaw) : null;
      if (audioTrack) {
        await room.localParticipant.publishTrack(audioTrack, { source: Track.Source.Microphone });
        console.info('[GUEST] audio track published');
      }
      if (videoTrack) {
        await room.localParticipant.publishTrack(videoTrack, { source: Track.Source.Camera });
        console.info('[GUEST] video track published');
      }
      liveDiag('tracks_published', { live_id: live?.id, audio: !!audioTrack, video: !!videoTrack }); // [LIVE-DIAG]
      localTracksRef.current = { audio: audioTrack, video: videoTrack };
      if (entry) entry.localTracks = localTracksRef.current;
      setMicOn(!!audioTrack);
      setCamOn(!!videoTrack);
      // Streams are ACTIVE → only now mark the guest on_stage server-side.
      try { await joinStage(live.id); } catch (e) { console.warn('[GUEST] joinStage API failed', e?.message || e); }
      try { onStageChanged?.(); } catch (_e) { /* noop */ }
      setStage('live');
      liveDiag('stage_joined', { live_id: live?.id }); // [LIVE-DIAG]
      console.info('[GUEST] on stage — publishing OK');
    } catch (err) {
      console.error('[GUEST] goOnStage failed', err);
      try { stream?.getTracks().forEach((tr) => tr.stop()); } catch (_e) { /* noop */ }
      const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      liveDiag(denied ? 'media_permission_denied' : 'stage_join_failed', { live_id: live?.id, err: err?.name || String(err?.message || 'unknown').slice(0, 60) }); // [LIVE-DIAG]
      setStageError(denied ? t('viewer.cohost_permission_denied') : t('viewer.stage_error_generic'));
      setStage('error');
    } finally {
      stageBusyRef.current = false;
    }
  };

  /** Leave the stage: stop streams, back to plain viewer (new request needed). */
  const leaveStage = useCallback(async () => {
    if (stageBusyRef.current) return;
    stageBusyRef.current = true;
    liveDiag('stage_leave_tap', { live_id: live?.id }); // [LIVE-DIAG]
    try {
      console.info('[GUEST] leaveStage — stopping streams');
      const entry = viewerCache.get(cacheKeyFor(live.id, 'cohost'));
      const room = entry?.room;
      const { audio, video } = localTracksRef.current;
      for (const tr of [audio, video]) {
        if (!tr) continue;
        try { await room?.localParticipant?.unpublishTrack(tr); } catch (_e) { /* noop */ }
        try { tr.stop(); } catch (_e) { /* noop */ }
      }
      localTracksRef.current = { audio: null, video: null };
      if (entry) entry.localTracks = { audio: null, video: null };
      setStage('idle');
      try { await removeGuest(live.id, currentUser?.id); } catch (e) { console.warn('[GUEST] removeGuest failed', e?.message || e); }
      liveDiag('stage_left_cleanup_done', { live_id: live?.id }); // [LIVE-DIAG]
      try { onStageChanged?.(); } catch (_e) { /* noop */ }
    } finally {
      stageBusyRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.id, currentUser?.id, onStageChanged]);

  // Sidebar "Lascia la partecipazione" button → same single leave flow.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.liveId && e.detail.liveId !== live?.id) return;
      leaveStage();
    };
    window.addEventListener('nymia:leave-stage', handler);
    return () => window.removeEventListener('nymia:leave-stage', handler);
  }, [leaveStage, live?.id]);

  const toggleMic = () => {
    const tr = localTracksRef.current.audio;
    if (!tr) return;
    const newState = !micOn;
    if (newState) tr.unmute(); else tr.mute();
    setMicOn(newState);
  };
  const toggleCam = () => {
    const tr = localTracksRef.current.video;
    if (!tr) return;
    const newState = !camOn;
    if (newState) tr.unmute(); else tr.mute();
    setCamOn(newState);
  };

  const onStage = role === 'cohost' && stage === 'live';

  // ---- Stage overlay (accepted / publishing / error states) ----
  const stageOverlay = (() => {
    if (role !== 'cohost' || stage === 'live') return null;
    if (status !== 'live' && status !== 'connecting') return null;
    if (stage === 'publishing') {
      return (
        <div className="absolute inset-x-0 bottom-0 z-20 flex justify-center p-4" data-testid="stage-publishing-overlay">
          <div className="flex items-center gap-2 rounded-full bg-black/80 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> {t('viewer.stage_publishing')}
          </div>
        </div>
      );
    }
    if (stage === 'error') {
      return (
        <div className="absolute inset-x-0 bottom-0 z-20 flex justify-center p-4" data-testid="stage-error-overlay">
          <div className="w-full max-w-sm rounded-2xl border border-red-400/40 bg-black/85 p-4 text-center backdrop-blur">
            <AlertTriangle className="mx-auto h-5 w-5 text-red-400" />
            <p className="mt-2 text-xs font-medium text-white/90" data-testid="stage-error-message">{stageError}</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={goOnStage}
                className="flex h-11 flex-1 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-white transition-colors hover:bg-emerald-600"
                data-testid="stage-retry-btn"
              >
                {t('viewer.stage_retry')}
              </button>
              <button
                onClick={leaveStage}
                className="flex h-11 flex-1 items-center justify-center rounded-full border border-white/25 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
                data-testid="stage-cancel-btn"
              >
                {t('viewer.stage_stay_viewer')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    // stage === 'idle' → invite to go on stage (big, explicit tap)
    const resumed = cohostStatus === 'on_stage';
    return (
      <div className="absolute inset-x-0 bottom-0 z-20 flex justify-center p-4" data-testid="go-on-stage-banner">
        <div className="w-full max-w-sm rounded-2xl border border-emerald-400/40 bg-black/85 p-4 text-center backdrop-blur">
          <p className="flex items-center justify-center gap-1.5 text-sm font-bold text-emerald-300">
            <CheckCircle2 className="h-4 w-4" />
            {resumed ? t('viewer.stage_resume_title') : t('viewer.stage_accepted_title')}
          </p>
          <p className="mt-1 text-xs text-white/70">
            {resumed ? t('viewer.stage_resume_desc') : t('viewer.stage_accepted_desc')}
          </p>
          <button
            onClick={goOnStage}
            className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-emerald-500 text-base font-bold text-white transition-transform hover:scale-[1.02]"
            data-testid="go-on-stage-btn"
          >
            <Hand className="h-5 w-5" /> {t('viewer.stage_join_btn')}
          </button>
        </div>
      </div>
    );
  })();

  // ---- Audio unlock banner (autoplay blocked by the browser) ----
  const audioUnlockBanner = (audioBlocked && status === 'live') ? (
    <div className="absolute inset-x-0 top-3 z-30 flex justify-center px-4" data-testid="audio-blocked-banner">
      <button
        type="button"
        onClick={enableAudio}
        className="flex h-11 items-center gap-2 rounded-full bg-amber-400 px-5 text-sm font-bold text-black shadow-lg transition-transform hover:scale-[1.03]"
        data-testid="enable-audio-btn"
      >
        <Volume2 className="h-5 w-5" /> {t('viewer.enable_audio_btn')}
      </button>
    </div>
  ) : null;

  // ---- On-stage controls (mic / cam / leave) ----
  const stageControls = onStage ? (
    <div className="absolute bottom-3 left-3 z-10 flex items-center gap-2">
      <button
        onClick={toggleMic}
        className={`flex h-9 w-9 items-center justify-center rounded-full ${micOn ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}
        data-testid="cohost-toggle-mic-btn"
        aria-label={t('viewer.cohost_mic_label')}
      >
        {micOn ? <Mic2 className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
      </button>
      {!audioOnly && (
        <button
          onClick={toggleCam}
          className={`flex h-9 w-9 items-center justify-center rounded-full ${camOn ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}
          data-testid="cohost-toggle-cam-btn"
          aria-label={t('viewer.cohost_cam_label')}
        >
          {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
        </button>
      )}
      <button
        onClick={leaveStage}
        className="flex h-9 items-center gap-1.5 rounded-full bg-red-500 px-3 text-xs font-bold text-white transition-colors hover:bg-red-600"
        data-testid="leave-stage-btn"
      >
        <LogOut className="h-3.5 w-3.5" /> {t('viewer.stage_leave_short')}
      </button>
    </div>
  ) : null;

  if (status === 'ended') {
    return (
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-neutral-950 text-white"
        data-testid="live-ended-overlay"
      >
        <p className="text-lg font-semibold">{t('viewer.live_ended_title')}</p>
        <p className="text-xs text-white/60">{t('viewer.live_ended_desc')}</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-6 text-center" data-testid="livekit-viewer-error">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
        <p className="text-sm font-semibold">{errorMsg}</p>
      </div>
    );
  }

  if (audioOnly) {
    return (
      <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-card" data-testid="livekit-viewer-audio">
        <Mic className="h-12 w-12 gold-text" />
        <span className="ml-3 font-semibold">{t('viewer.audio_live_label')}</span>
        <audio ref={audioRef} autoPlay />
        {remoteGuests.length > 0 && (
          <div className="absolute left-3 top-3 flex flex-col gap-2" data-testid="viewer-guest-tiles-audio">
            {remoteGuests.map((g) => <GuestVideoTile key={g.identity} guest={g} />)}
          </div>
        )}
        {audioUnlockBanner}
        {stageControls}
        {stageOverlay}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 rounded-lg bg-black" data-testid="livekit-viewer">
      <video ref={videoRef} autoPlay playsInline className="h-full w-full object-contain" />
      <audio ref={audioRef} autoPlay />
      {remoteGuests.length > 0 && (
        <div
          className="absolute left-3 top-12 z-10 flex flex-col gap-2"
          data-testid="viewer-guest-tiles"
        >
          {remoteGuests.map((g) => <GuestVideoTile key={g.identity} guest={g} />)}
        </div>
      )}
      {status === 'live' && (
        <LiveChatOverlay messages={chat.messages} visibleCount={5} fadeMs={8000} />
      )}
      {/* Local self-preview in the bottom-right while on stage (mirrored). */}
      {onStage && (
        <div className="absolute bottom-3 right-3 z-10 h-28 w-20 overflow-hidden rounded-lg border border-emerald-500/60 bg-black shadow-lg" data-testid="cohost-self-preview">
          <video
            ref={selfPreviewRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          <div className="absolute left-1 top-1 rounded-sm bg-emerald-500/90 px-1 py-0.5 text-[9px] font-bold uppercase text-white">{t('viewer.cohost_you_badge')}</div>
        </div>
      )}
      {audioUnlockBanner}
      {stageControls}
      {stageOverlay}
      {status === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t('viewer.connecting_short')}
        </div>
      )}
    </div>
  );
};

export default LiveKitViewer;
